import React from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BookOpenIcon,
  UsersIcon,
  BarChart3Icon,
  ZapIcon,
  LayersIcon,
  Gamepad2Icon,
  TargetIcon,
  SparklesIcon
} from 'lucide-react';

const ReportsLayout = () => {
  const { t } = useTranslation();

  const links = [
    { to: 'daily-exercises-ranking', label: t('reports.tabDailyRankingShort'), icon: BookOpenIcon },
    { to: 'users', label: t('reports.tabUsersShort'), icon: UsersIcon },
    { to: 'daily-exercises', label: t('reports.tabDailyExercisesShort'), icon: BarChart3Icon },
    { to: 'flash-anzan', label: t('reports.tabFlashAnzanShort'), icon: ZapIcon },
    { to: 'flash-cards', label: t('reports.tabFlashCardsShort'), icon: LayersIcon },
    { to: 'games', label: t('reports.tabGamesShort'), icon: Gamepad2Icon },
    { to: 'challenges', label: t('reports.tabChallengesShort'), icon: TargetIcon }
  ];

  return (
    <div className="space-y-8">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-2xl border border-slate-200/90 bg-white/90 shadow-sm ring-1 ring-slate-900/[0.04] backdrop-blur-sm">
        <div
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(900px_circle_at_0%_0%,rgba(14,165,233,0.11),transparent_55%),radial-gradient(700px_circle_at_100%_20%,rgba(217,70,239,0.06),transparent_50%)]"
          aria-hidden
        />
        <div className="relative flex flex-col gap-6 p-6 sm:flex-row sm:items-start sm:justify-between sm:p-8">
          <div className="max-w-2xl space-y-2">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-600/90">
              {t('navigation.reports')}
            </p>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              {t('reports.title')}
            </h1>
            <p className="text-sm leading-relaxed text-slate-600 sm:text-base">
              {t('reports.layoutHeroDescription')}
            </p>
          </div>
          <div className="flex shrink-0 items-center justify-center sm:pt-1">
            <div className="rounded-2xl border border-primary-100 bg-gradient-to-br from-primary-50 to-white p-4 shadow-inner shadow-primary-500/5">
              <SparklesIcon className="h-9 w-9 text-primary-500 sm:h-10 sm:w-10" strokeWidth={1.5} aria-hidden />
            </div>
          </div>
        </div>

        {/* Segmented navigation */}
        <div className="relative border-t border-slate-100/90 bg-slate-50/80 px-3 pb-3 pt-3 sm:px-4">
          <nav
            className="flex gap-1.5 overflow-x-auto pb-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            aria-label="Report sections"
          >
            {links.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  [
                    'group flex min-h-[44px] shrink-0 items-center gap-2 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-all duration-200 sm:px-4',
                    isActive
                      ? 'bg-white text-primary-700 shadow-md shadow-slate-900/8 ring-1 ring-slate-900/[0.06]'
                      : 'text-slate-600 hover:bg-white/90 hover:text-slate-900 hover:shadow-sm'
                  ].join(' ')
                }
              >
                <Icon
                  className="h-[1.125rem] w-[1.125rem] shrink-0 opacity-80 transition-opacity group-hover:opacity-100"
                  strokeWidth={2}
                />
                <span className="whitespace-nowrap">{label}</span>
              </NavLink>
            ))}
          </nav>
        </div>
      </section>

      {/* Report content */}
      <div className="relative">
        <Outlet />
      </div>
    </div>
  );
};

export default ReportsLayout;
